/**
 * assembleAnswerDetail — merge a question's server-cached rows with its pending
 * outbox edits into the optimistic AnswerDetail the UI renders. Shared by every
 * Repo adapter so the local-first overlay behaves identically on web (Dexie)
 * and native (SQLite).
 */
import type {
  Inspection,
  QuestionAnswer,
  QuestionAspectResponse,
  InspectionHumanRankResponse,
  PhotoComparison,
  NegativeObservation,
} from "@/types/api";
import type { OutboxMutation, AnswerDetail } from "./types";

const ACTIVE = new Set(["pending", "syncing", "failed"]);

/**
 * Stamp the vessel/inspection columns the global Observations list groups,
 * filters and displays by onto a synthesized (offline-queued) NOM, read from the
 * cached inspection row. Without this an offline NOM has no vesselId/type, so it
 * lands outside any vessel group and gets filtered out of the global list. The
 * cached inspection row comes from the list endpoint (vesselId + type always
 * present; vessel name resolved by the list's own vessel lookup when absent). */
export function withInspectionMeta(
  nom: NegativeObservation,
  insp: Inspection | undefined,
): NegativeObservation {
  if (!insp) return nom;
  const i = insp as Inspection & {
    vesselName?: string | null; vesselImo?: string | null;
    vessel?: { name?: string | null; imo?: string | null } | null;
    checklist?: { name?: string | null } | null; checklistName?: string | null;
  };
  return {
    ...nom,
    vesselId: i.vesselId ?? null,
    vesselName: i.vesselName ?? i.vessel?.name ?? null,
    vesselImo: i.vesselImo ?? i.vessel?.imo ?? null,
    inspectionType: i.type ?? null,
    checklistName: i.checklist?.name ?? i.checklistName ?? null,
  } as NegativeObservation;
}

/**
 * Stable synthetic envelope id for a question whose server `question_answers`
 * row hasn't been cached yet (created/edited offline, not yet synced). Negative
 * + derived from the questionId so it is (a) distinct per question — real ids
 * are positive — and (b) identical across reads. The observations overlay
 * resolves a queued NOM's `answerId` the same way, so an offline NOM lands on
 * its own question's card instead of every uncached question sharing id -1.
 */
export function synthEnvelopeId(questionId: number): number {
  return -(1_000_000_000 + questionId);
}

/** A queued (offline-created) NOM's synthetic id — derived from its stable
 *  outbox row id, negated so it can never collide with a real (positive) id. */
function synthNomId(outboxRowId: number): number {
  return -(1 + outboxRowId);
}

/**
 * Turn the outbox's queued `nom` creates for an inspection into optimistic
 * {@link NegativeObservation} rows so they appear in the observations view (the
 * per-question chips AND the list) immediately — before they sync. Without this
 * a NOM raised offline lived only in the outbox: it vanished the moment the
 * question was saved (its `pendingNoms` entry was cleared) and never reappeared
 * until a sync round-trip.
 *
 * `answerId` is resolved from the cached envelope when present, else the same
 * {@link synthEnvelopeId} the answer-detail overlay assigns — keeping the NOM
 * matched to its question's envelope id either way.
 */
export function synthesizeQueuedNoms(
  queued: OutboxMutation[],
  /** Resolve the CACHED server envelope id for a (inspection, question), or
   *  undefined when none is cached yet — in which case the stable
   *  {@link synthEnvelopeId} is used. Lets a single inspection (questionId-keyed
   *  map) and the global list (inspection+question-keyed map) share this code. */
  cachedAnswerId: (inspectionId: number, questionId: number) => number | undefined,
  /** When set, only NOMs for this inspection are synthesized. */
  onlyInspectionId?: number,
): NegativeObservation[] {
  const out: NegativeObservation[] = [];
  for (const m of queued) {
    if (m.entity !== "nom" || m.id == null || !ACTIVE.has(m.state)) continue;
    const p = m.payload;
    const inspectionId = (p.inspectionId as number | undefined) ?? m.inspectionId;
    if (inspectionId == null) continue;
    if (onlyInspectionId != null && inspectionId !== onlyInspectionId) continue;
    const questionId = (p.questionId as number | undefined) ?? m.questionId ?? null;
    const answerId =
      questionId != null
        ? (cachedAnswerId(inspectionId, questionId) ?? synthEnvelopeId(questionId))
        : null;
    out.push({
      id: synthNomId(m.id),
      inspectionId,
      answerId,
      aspectResponseId: null,
      photoComparisonId: null,
      aspect: (p.aspect as NegativeObservation["aspect"]) ?? "hardware",
      severity: (p.severity as NegativeObservation["severity"]) ?? "concern",
      riskRating: null,
      findingTypeId: null,
      status: "open",
      socSource: null,
      socHardwareId: (p.socHardwareId as string | null | undefined) ?? null,
      socProcessId: (p.socProcessId as string | null | undefined) ?? null,
      rankId: (p.rankId as number | null | undefined) ?? null,
      nocHardwareId: (p.nocHardwareId as string | null | undefined) ?? null,
      nocProcessId: (p.nocProcessId as string | null | undefined) ?? null,
      nocPhotoId: (p.nocPhotoId as string | null | undefined) ?? null,
      nocOtherText: null,
      pifIds: (p.pifIds as string[] | undefined) ?? [],
      title: (p.title as string | undefined) ?? "",
      description: (p.description as string | undefined) ?? "",
      inspectorComment: null,
      locationDetail: (p.locationDetail as string | null | undefined) ?? null,
      operatorResponse: null,
      operatorResponseBy: null,
      operatorResponseAt: null,
      raisedBy: null,
      closedBy: null,
      closedAt: null,
      reviewStatus: "pending",
      reviewedBy: null,
      reviewedAt: null,
      reviewNote: null,
      createdAt: m.createdAt,
      updatedAt: m.clientUpdatedAt ?? m.createdAt,
    });
  }
  return out;
}

export function assembleAnswerDetail(args: {
  inspectionId: number;
  questionId: number;
  answerId: number | null;
  cachedEnvelope: QuestionAnswer | null;
  cachedAspects: QuestionAspectResponse[];
  cachedHuman: InspectionHumanRankResponse[];
  cachedPhoto: PhotoComparison[];
  pending: OutboxMutation[];
}): AnswerDetail {
  const { inspectionId, questionId, answerId, cachedEnvelope, cachedAspects, cachedHuman, cachedPhoto } = args;
  const pending = args.pending.filter((m) => ACTIVE.has(m.state));

  let envelope: QuestionAnswer | null = cachedEnvelope;
  const aspectByKey = new Map<string, QuestionAspectResponse>();
  for (const a of cachedAspects) aspectByKey.set(a.aspect, a);
  const humanByRank = new Map<number, InspectionHumanRankResponse>();
  for (const h of cachedHuman) humanByRank.set(h.rankId, h);
  let photo: PhotoComparison | null = cachedPhoto[0] ?? null;

  const synthId = (cur: { id?: number } | null | undefined) => cur?.id ?? -1;
  for (const m of pending) {
    const p = m.payload;
    if (m.entity === "envelope") {
      envelope = {
        ...(envelope ?? ({ id: synthEnvelopeId(questionId), inspectionId, questionId } as unknown as QuestionAnswer)),
        value: p.value,
        comment: (p.comment as string | null) ?? null,
        primaryAnswer: (p.primaryAnswer as QuestionAnswer["primaryAnswer"]) ?? (envelope?.primaryAnswer ?? null),
        // SIRE 1.7 N/A reason/comment — only overwritten when the mutation
        // carried them (sire17 standard saves); otherwise keep the cached value.
        naReason: p.naReason !== undefined ? (p.naReason as QuestionAnswer["naReason"]) : (envelope?.naReason ?? null),
        naComment: p.naComment !== undefined ? (p.naComment as string | null) : (envelope?.naComment ?? null),
      } as QuestionAnswer;
    } else if (m.entity === "aspect" && m.aspect) {
      const base = aspectByKey.get(m.aspect);
      aspectByKey.set(m.aspect, {
        ...(base ?? ({ id: synthId(base), answerId: answerId ?? -1, aspect: m.aspect } as unknown as QuestionAspectResponse)),
        tool: (p.tool as QuestionAspectResponse["tool"]) ?? base?.tool ?? "graduated",
        binaryValue: (p.binaryValue as QuestionAspectResponse["binaryValue"]) ?? null,
        graduatedValue: (p.graduatedValue as QuestionAspectResponse["graduatedValue"]) ?? null,
        comment: (p.comment as string | null) ?? null,
        notAnsweredReason: (p.notAnsweredReason as QuestionAspectResponse["notAnsweredReason"]) ?? null,
        notAnsweredComment: (p.notAnsweredComment as string | null) ?? null,
      } as QuestionAspectResponse);
    } else if (m.entity === "humanRank" && m.rankId != null) {
      const base = humanByRank.get(m.rankId);
      humanByRank.set(m.rankId, {
        ...(base ?? ({ id: synthId(base), answerId: answerId ?? -1, rankId: m.rankId } as unknown as InspectionHumanRankResponse)),
        graduatedValue: (p.graduatedValue as InspectionHumanRankResponse["graduatedValue"]) ?? null,
        comment: (p.comment as string | null) ?? null,
        pifIds: (p.pifIds as string[]) ?? [],
        title: (p.title as string | null) ?? null,
        description: (p.description as string | null) ?? null,
        locationDetail: (p.locationDetail as string | null) ?? null,
      } as InspectionHumanRankResponse);
    } else if (m.entity === "photoComparison") {
      photo = {
        ...(photo ?? ({ id: synthId(photo), answerId: answerId ?? -1 } as unknown as PhotoComparison)),
        verdict: (p.verdict as PhotoComparison["verdict"]) ?? null,
        comment: (p.comment as string | null) ?? null,
        natureOfConcernId: (p.natureOfConcernId as string | null) ?? null,
        natureOfConcernText: (p.natureOfConcernText as string | null) ?? null,
      } as PhotoComparison;
    }
  }

  return {
    envelope,
    aspects: [...aspectByKey.values()],
    humanRankResponses: [...humanByRank.values()],
    photoComparisons: photo ? [photo] : [],
  };
}
